/**
 * SEO HEADING STRUCTURE - Study Timer
 * ====================================
 * 
 * TITLE: Study Timer - Free Pomodoro Timer | CSW | Online Workspace...
 * META DESCRIPTION: Free online Pomodoro timer for studying...
 * 
 * H1 (1 tag):
 *   - "Study Timer" (hero section)
 * 
 * H2 (3 tags):
 *   - "Focus Features" (features section)
 *   - "Build Better Study Habits" (benefits section)
 *   - "Ready to Focus?" (CTA section)
 * 
 * H3 (8 tags):
 *   - Feature card titles (4): "Pomodoro Timer", "Custom Sessions", etc.
 *   - Benefit titles (4): Dynamic benefit items
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Focus, TrendingUp, Zap, ArrowRight, Timer, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const timerAlternatives = [
  { name: "Pomodor", path: "/study-timer/csw-vs-pomodor/" },
  { name: "Otto", path: "/study-timer/csw-vs-otto/" },
  { name: "Forest", path: "/study-timer/csw-vs-forest/" },
  { name: "Session", path: "/study-timer/csw-vs-session/" },
  { name: "Flow", path: "/study-timer/csw-vs-flow/" },
  { name: "Toggl Track", path: "/study-timer/csw-vs-toggl-track/" },
  { name: "Focus Booster", path: "/study-timer/csw-vs-focus-booster/" },
  { name: "Be Focused", path: "/study-timer/csw-vs-be-focused/" },
  { name: "PomoDone", path: "/study-timer/csw-vs-pomodone/" },
  { name: "Pomofocus", path: "/study-timer/csw-vs-pomofocus/" },
  { name: "Focus Keeper", path: "/study-timer/csw-vs-focus-keeper/" },
  { name: "Minimalist", path: "/study-timer/csw-vs-minimalist/" },
  { name: "Flocus", path: "/study-timer/csw-vs-flocus/" },
  { name: "Taskade", path: "/study-timer/csw-vs-taskade/" },
  { name: "Focus@Will", path: "/study-timer/csw-vs-focus-at-will/" },
  { name: "Cold Turkey", path: "/study-timer/csw-vs-cold-turkey/" },
  { name: "Brain.fm", path: "/study-timer/csw-vs-brain-fm/" },
  { name: "Freedom", path: "/study-timer/csw-vs-freedom/" },
];

const features = [
  {
    icon: Clock,
    title: "Pomodoro Timer",
    description: "Classic 25-minute work sessions with automatic breaks",
  },
  {
    icon: Focus,
    title: "Custom Sessions",
    description: "Set your own work and break durations to match your rhythm",
  },
  {
    icon: TrendingUp,
    title: "Session Tracking",
    description: "Monitor completed sessions and build daily streaks",
  },
  {
    icon: Zap,
    title: "Quick Start",
    description: "One click to begin. No setup, no distractions",
  },
];

const StudyTimer = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study Timer - Free Pomodoro Timer"
        description="Free online Pomodoro timer for studying. Track sessions, build streaks, and stay focused with customizable work and break intervals."
        keywords="study timer, pomodoro timer, focus timer, online timer, productivity timer, pomodoro technique"
        canonicalUrl="/study-timer/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Pomodoro Technique</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Study Timer</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Time-boxing made simple. Stay focused with Pomodoro techniques and build consistent study habits.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Timing Now
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Try for Free
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">25min</p>
                    <p className="text-sm text-muted-foreground">Focus Sessions</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">5min</p>
                    <p className="text-sm text-muted-foreground">Break Time</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Stay Focused</p>
                      <p className="text-xl font-bold text-foreground">Track Your Time</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Timer className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Focus{" "}
                <span className="text-gradient">Features</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Simple yet powerful tools to help you stay productive
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                How It{" "}
                <span className="text-gradient">Works</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Three simple steps to achieve deep focus
              </p>
            </div>
            
            <div className="space-y-8">
              {[
                { step: 1, title: "Choose Your Duration", desc: "Select a standard 25-minute Pomodoro or customize your work session length" },
                { step: 2, title: "Focus Deeply", desc: "Work without interruption while the timer counts down silently" },
                { step: 3, title: "Take a Break", desc: "Get notified when it's time to rest, then start your next session refreshed" },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-xl font-display group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 font-display">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Start Your First{" "}
              <span className="text-gradient">Session</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join thousands using CSW's Study Timer to stay focused and productive.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Try Timer Now
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        {/* Compare Timer Tools Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Compare{" "}
                <span className="text-gradient">Timer Tools</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                See how CSW compares to other popular Pomodoro and focus apps
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {timerAlternatives.map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                >
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    CSW vs {tool.name}
                  </span>
                  <ExternalLink className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyTimer;